import {
    isPlayerAction,
    PlayerId,
    Projection,
    SharedGameState,
    useGameStore,
} from '@glark-newco/game-library';
import {useEffect, useRef, useState} from 'react';
import {
    isTransferRingEvent,
    TransferRingEvent,
} from '../SupplyChainPayloadTypes';

type TransferEventListener = (e: TransferRingEvent) => void;

export interface RingTransferListenerSlice extends SharedGameState {
    /** Set of callbacks that want to be notified of every new TransferRingEvent */
    __transferListeners?: Set<TransferEventListener>;
}

export const ringTransferListenerProjection: Projection<RingTransferListenerSlice> = {
    name: 'ringTransferListenerProjection',
    project: (state, newEvents) => {
        const listeners = state.__transferListeners ?? new Set<TransferEventListener>();

        // console.log('ringTransferListenerProjection processing ', newEvents);
        newEvents.forEach(ev => {
            if (isPlayerAction(ev) && isTransferRingEvent(ev.payload)) {
                const payload = ev.payload;
                listeners.forEach(cb => cb(payload));
            }
        });

        // This projection doesn't push anything to the store.
        return {};
    },
    reset: () => ({__transferListeners: new Set()}),
};

const addRingTransferListener = (cb: TransferEventListener) => {
    useGameStore.setState((s: RingTransferListenerSlice) => {
        const set = s.__transferListeners ?? new Set<TransferEventListener>();
        set.add(cb);
        return {__transferListeners: set};
    });
};

const removeRingTransferListener = (cb: TransferEventListener) => {
    useGameStore.setState((s: RingTransferListenerSlice) => {
        const set = s.__transferListeners;
        if (!set) return {};
        set.delete(cb);
        return {__transferListeners: set};
    });
};

export const useTransferRingListenerForLink = (
    playerA: PlayerId,
    playerB: PlayerId,
) :[TransferRingEvent[], (event: TransferRingEvent) => void] => {
    const [queue, setQueue] = useState<TransferRingEvent[]>([]);

    // Keep the latest `playerA / playerB` values in a ref so the callback
    // does not need to be recreated on every render.
    const playersRef = useRef({a: playerA, b: playerB});
    useEffect(() => {
        playersRef.current = {a: playerA, b: playerB};
    }, [playerA, playerB]);

    // Register the listener once on mount and deregister on unmount
    useEffect(() => {
        const handler = (event: TransferRingEvent) => {
            // console.log(`useTransferRingListenerForLink handling event for ${JSON.stringify(event)}`);
            const {a, b} = playersRef.current;
            const isRelevant =
                (event.sourcePlayerId === a && event.recipientPlayerId === b) ||
                (event.sourcePlayerId === b && event.recipientPlayerId === a);
            if (isRelevant) {
                setQueue(prev => [...prev, event]);
            }
        };
        addRingTransferListener(handler);
        return () => {
            removeRingTransferListener(handler);
        };
    }, []);

    // Helper to acknowledge (remove) the first event that has been animated
    const acknowledge = (event: TransferRingEvent) => {
        setQueue(prev => prev.filter(e => e !== event));
    };

    return [queue, acknowledge];
};
